/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: AgentBehaviorSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { Settings } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { FormState } from './types/agents.types';

interface AgentBehaviorSectionProps {
  formData: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
}

export function AgentBehaviorSection({
  formData,
  onFieldChange
}: AgentBehaviorSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('agents.form.sections.behavior')} icon={<Settings className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Max No Answer */}
        <SimpleTooltip content={t('agents.form.fields.maxNoAnswer.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="maxNoAnswer" className="text-left">
            {t('agents.form.fields.maxNoAnswer.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="maxNoAnswer"
            type="number"
            min="0"
            value={formData.maxNoAnswer}
            onChange={(event) => onFieldChange('maxNoAnswer', event.target.value)}
            placeholder={t('agents.form.fields.maxNoAnswer.placeholder')}
          />
        </div>

        {/* Busy Delay Time */}
        <SimpleTooltip content={t('agents.form.fields.busyDelayTime.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="busyDelayTime" className="text-left">
            {t('agents.form.fields.busyDelayTime.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="busyDelayTime"
            type="number"
            min="0"
            value={formData.busyDelayTime}
            onChange={(event) => onFieldChange('busyDelayTime', event.target.value)}
            placeholder={t('agents.form.fields.busyDelayTime.placeholder')}
          />
        </div>

        {/* Reject Delay Time */}
        <SimpleTooltip content={t('agents.form.fields.rejectDelayTime.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="rejectDelayTime" className="text-left">
            {t('agents.form.fields.rejectDelayTime.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="rejectDelayTime"
            type="number"
            min="0"
            value={formData.rejectDelayTime}
            onChange={(event) => onFieldChange('rejectDelayTime', event.target.value)}
            placeholder={t('agents.form.fields.rejectDelayTime.placeholder')}
          />
        </div>

        {/* No Answer Delay Time */}
        <SimpleTooltip content={t('agents.form.fields.noAnswerDelayTime.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="noAnswerDelayTime" className="text-left">
            {t('agents.form.fields.noAnswerDelayTime.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="noAnswerDelayTime"
            type="number"
            min="0"
            value={formData.noAnswerDelayTime}
            onChange={(event) => onFieldChange('noAnswerDelayTime', event.target.value)}
            placeholder={t('agents.form.fields.noAnswerDelayTime.placeholder')}
          />
        </div>

        {/* Contact Timeout */}
        <SimpleTooltip content={t('agents.form.fields.contactTimeout.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="contactTimeout" className="text-left">
            {t('agents.form.fields.contactTimeout.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="contactTimeout"
            type="number"
            min="1"
            value={formData.contactTimeout}
            onChange={(event) => onFieldChange('contactTimeout', event.target.value)}
            placeholder={t('agents.form.fields.contactTimeout.placeholder')}
          />
        </div>

        {/* Wrap-Up Time */}
        <SimpleTooltip content={t('agents.form.fields.wrapUpTime.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="wrapUpTime" className="text-left">
            {t('agents.form.fields.wrapUpTime.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="wrapUpTime"
            type="number"
            min="0"
            value={formData.wrapUpTime}
            onChange={(event) => onFieldChange('wrapUpTime', event.target.value)}
            placeholder={t('agents.form.fields.wrapUpTime.placeholder')}
          />
        </div>
      </div>
    </FormBox>
  );
}

